import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { placeService, reportService } from '../services/api';
import { formatTimeAgo } from '../utils/dateUtils';
import { currentSessionId } from '../utils/session';

import echo from '../services/echo';

const PinCard = ({ place, onDeleted, onUpdated }) => {
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportLoading, setReportLoading] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);
  const sessionId = currentSessionId;

  // Keep onUpdated stable for WebSocket listeners
  const onUpdatedRef = React.useRef(onUpdated);
  useEffect(() => {
    onUpdatedRef.current = onUpdated;
  }, [onUpdated]);

  // Local state for user interactions (not data)
  const [hasLiked, setHasLiked] = useState(
    localStorage.getItem(`place_liked_${place.id}`) === 'true'
  );
  const [hasVotedDelete, setHasVotedDelete] = useState(
    localStorage.getItem(`place_vote_delete_${place.id}`) === 'true'
  );

  // Comments state
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  // Image viewer state
  const [showImageModal, setShowImageModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Compute available images
  const allImages = React.useMemo(() => {
    if (place.images && place.images.length > 0) {
      return place.images.map(img => img.image_url);
    }
    return place.image_url ? [place.image_url] : [];
  }, [place.images, place.image_url]);

  const activeImage = allImages[currentImageIndex];

  // Spotify Embed Helper
  const getSpotifyEmbedUrl = (url) => {
    if (!url) return null;
    try {
      // Handle "spotify:track:..." URI or "https://open.spotify.com/track/..."
      const match = url.match(/spotify\.com\/(track|album|playlist)\/([a-zA-Z0-9]+)/);
      if (match) {
        const [, type, id] = match;
        return `https://open.spotify.com/embed/${type}/${id}?utm_source=generator&theme=0`;
      }
      return null;
    } catch (e) {
      return null;
    }
  };

  const spotifyEmbedUrl = getSpotifyEmbedUrl(place.spotify_url);

  // TEMPORARILY DISABLED: expiration status UI
  // const { label: expiryLabel, isExpiring } = getExpiryStatus(place.expires_at);

  // Real-time updates
  useEffect(() => {
    const channel = echo.channel(`place.${place.id}`);

    channel.listen('PlaceUpdated', (e) => {
      // Skip if this event was triggered by the current user
      if (e.metadata?.sessionId === sessionId) {
        return;
      }

      switch (e.action) {
        case 'liked':
          if (onUpdatedRef.current) {
            onUpdatedRef.current({
              id: place.id,
              likes_count: e.metadata.likes_count,
              expires_at: e.place?.expires_at || place.expires_at
            });
          }
          break;

        case 'commented':
          // Update comments list if we're showing it
          setComments(prev => {
            const newComment = e.metadata.comment;
            // Avoid duplicates
            if (prev.some(c => c.id === newComment.id)) return prev;
            return [newComment, ...prev];
          });

          // Update comment count on card
          if (onUpdatedRef.current) {
            const newCount = e.place?.comments_count ?? ((place.comments_count || 0) + 1);
            onUpdatedRef.current({
              id: place.id,
              comments_count: newCount
            });
          }
          break;

        case 'vote-delete':
          if (onUpdatedRef.current) {
            onUpdatedRef.current({
              id: place.id,
              delete_votes: e.metadata.delete_votes
            });
          }
          break;

        case 'deleted':
          if (onDeleted) {
            onDeleted(place.id);
          }
          break;

        default:
          console.log('Unknown action:', e.action);
      }
    });

    return () => {
      echo.leave(`place.${place.id}`);
    };
  }, [place.id, place.comments_count, place.expires_at, onDeleted, sessionId]);

  const handleReport = async (e) => {
    e.preventDefault();

    if (!reportReason.trim()) {
      return;
    }

    setReportLoading(true);

    try {
      await reportService.create({
        place_id: place.id,
        reason: reportReason,
      });
      setReportSuccess(true);
      setReportReason('');
      setShowReportForm(false);
      setTimeout(() => setReportSuccess(false), 3000);
    } catch (error) {
      console.error('Error reporting place:', error);
    } finally {
      setReportLoading(false);
    }
  };

  const handleDelete = async () => {
    if (hasVotedDelete) return;

    setHasVotedDelete(true);
    localStorage.setItem(`place_vote_delete_${place.id}`, 'true');

    const previousVotes = place.delete_votes || 0;
    if (onUpdated) {
      onUpdated({ ...place, delete_votes: previousVotes + 1 });
    }

    try {
      const result = await placeService.voteDelete(place.id, sessionId);

      if (result.deleted) {
        onDeleted(place.id);
      } else {
        if (onUpdated) {
          onUpdated({ ...place, delete_votes: result.delete_votes });
        }
      }
    } catch (error) {
      console.error('Error voting to delete place:', error);
      setHasVotedDelete(false);
      localStorage.removeItem(`place_vote_delete_${place.id}`);
      if (onUpdated) {
        onUpdated({ ...place, delete_votes: previousVotes });
      }
    }
  };

  const handleLike = async () => {
    if (hasLiked) return;

    setHasLiked(true);
    localStorage.setItem(`place_liked_${place.id}`, 'true');

    const previousLikes = place.likes_count || 0;
    const previousExpiry = place.expires_at;

    if (onUpdated) {
      onUpdated({
        ...place,
        likes_count: previousLikes + 1,
      });
    }

    try {
      const result = await placeService.like(place.id, sessionId);

      if (onUpdated) {
        onUpdated({
          ...place,
          likes_count: result.likes_count,
          expires_at: result.expires_at || place.expires_at
        });
      }
    } catch (error) {
      console.error('Error liking place:', error);
      setHasLiked(false);
      localStorage.removeItem(`place_liked_${place.id}`);
      if (onUpdated) {
        onUpdated({
          ...place,
          likes_count: previousLikes,
          expires_at: previousExpiry
        });
      }
    }
  };

  const toggleComments = async () => {
    if (!showComments) {
      setShowComments(true);
      setLoadingComments(true);
      try {
        const data = await placeService.getComments(place.id);
        setComments(data);
      } catch (error) {
        console.error('Error fetching comments:', error);
      } finally {
        setLoadingComments(false);
      }
    } else {
      setShowComments(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmittingComment(true);
    try {
      const comment = await placeService.addComment(place.id, newComment, sessionId);
      setComments([comment, ...comments]);
      if (onUpdated) {
        onUpdated({ ...place, comments_count: (place.comments_count || 0) + 1 });
      }
      setNewComment('');
    } catch (error) {
      console.error('Error posting comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-0 overflow-hidden flex flex-col w-[300px] sm:w-[340px] max-h-[80vh]">

      {/* Header: Badges & Time */}
      <div className="px-4 py-3 flex justify-between items-start bg-gradient-to-b from-gray-50/50 to-transparent dark:from-gray-800/50">
        <div className="flex flex-col gap-1.5">
          <span className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">
            {formatTimeAgo(place.created_at)}
          </span>

          <div className="flex flex-wrap items-center gap-1.5">
            {/* Likes Badge */}
            <div className="flex items-center gap-1 bg-pink-50 text-pink-600 px-2 py-0.5 rounded-md border border-pink-100 text-xs font-bold">
              <span>♥</span>
              <span>{place.likes_count || 0}</span>
            </div>
            {/* Prestige Badges omitted for brevity but preserved logic if needed */}
            {(place.likes_count || 0) >= 50 && (
              <div className="flex items-center gap-1 bg-gradient-to-r from-orange-400 to-red-500 text-white px-1.5 py-0.5 rounded text-[10px] font-bold shadow-sm">
                <span>🔥</span><span>Hot</span>
              </div>
            )}
          </div>
        </div>

        {/* Vote Delete Button */}
        <button
          onClick={handleDelete}
          className={`flex items-center gap-1 px-2 py-1 rounded-full transition-all text-xs font-medium border ${hasVotedDelete
            ? 'bg-red-50 text-red-600 border-red-100 cursor-default'
            : 'text-gray-400 hover:text-red-500 hover:bg-red-50 border-transparent'
            }`}
          disabled={hasVotedDelete}
          title={hasVotedDelete ? 'You voted to delete' : 'Vote to delete this mark'}
        >
          <span>🗑️</span>
          {(place.delete_votes || 0) > 0 && <span>{place.delete_votes}</span>}
        </button>
      </div>

      {/* Scrollable Content Area */}
      <div className="overflow-y-auto flex-1 custom-scrollbar">
        {/* Image Carousel */}
        {activeImage && (
          <div className="px-4 mb-3">
            <div className="relative group overflow-hidden rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
              <img
                src={activeImage}
                alt={`Place ${currentImageIndex + 1}`}
                className="w-full h-40 object-cover cursor-pointer transition-transform duration-500 group-hover:scale-105"
                onClick={() => setShowImageModal(true)}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors pointer-events-none" />

              {/* Carousel Controls */}
              {allImages.length > 1 && (
                <>
                  <button onClick={prevImage} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-1 rounded-full hover:bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity">
                    ‹
                  </button>
                  <button onClick={nextImage} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-1 rounded-full hover:bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity">
                    ›
                  </button>
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                    {allImages.map((_, idx) => (
                      <div key={idx} className={`w-1.5 h-1.5 rounded-full ${idx === currentImageIndex ? 'bg-white' : 'bg-white/50'}`} />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <div className="px-4 pb-2">
          {/* Moods */}
          {place.moods && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {(typeof place.moods === 'string' ? JSON.parse(place.moods) : place.moods).map((mood) => (
                <span
                  key={mood}
                  className="inline-block bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-md border border-indigo-100 dark:border-indigo-800"
                >
                  {mood}
                </span>
              ))}
            </div>
          )}

          {/* Description */}
          <p className="text-gray-800 dark:text-gray-200 text-sm leading-relaxed mb-4 font-normal">
            {place.description}
          </p>

          {/* Spotify Embed */}
          {spotifyEmbedUrl && (
            <div className="mb-4 rounded-xl overflow-hidden shadow-sm border border-gray-100 dark:border-gray-700">
              <iframe
                src={spotifyEmbedUrl}
                width="100%"
                height="80"
                frameBorder="0"
                allowFullScreen=""
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                title="Spotify Embed"
              ></iframe>
            </div>
          )}

          {/* TEMPORARILY DISABLED: Expiry warning UI */}
          {/* {isExpiring && (
            <div className="mb-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/50 rounded-lg flex items-center gap-2">
              <span className="text-lg">⏳</span>
              <div className="text-xs text-yellow-800 dark:text-yellow-500">
                <span className="font-bold">{expiryLabel}</span> left. Like to extend!
              </div>
            </div>
          )} */}

          {/* Report Form Area */}
          {reportSuccess ? (
            <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded-lg text-xs font-medium mb-3 text-center">
              Report sent. Thanks for helping!
            </div>
          ) : showReportForm ? (
            <form onSubmit={handleReport} className="space-y-2 mb-3 bg-gray-50 p-3 rounded-lg border border-gray-200 animate-in fade-in zoom-in-95">
              <textarea
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                placeholder="Why are you reporting this?"
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-red-400"
                rows={2}
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowReportForm(false);
                    setReportReason('');
                  }}
                  className="flex-1 bg-white hover:bg-gray-100 text-gray-700 text-xs px-3 py-1.5 rounded border border-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={reportLoading || !reportReason.trim()}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white text-xs px-3 py-1.5 rounded font-medium shadow-sm disabled:opacity-50"
                >
                  {reportLoading ? 'Sending...' : 'Report'}
                </button>
              </div>
            </form>
          ) : null}

          {/* Actions Bar */}
          {!showReportForm && (
            <div className="flex items-center gap-2 mb-2">
              <button
                onClick={handleLike}
                disabled={hasLiked}
                className={`flex-1 h-9 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${hasLiked
                  ? 'bg-pink-100 text-pink-700 cursor-default border border-pink-200'
                  : 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0'
                  }`}
              >
                <span>{hasLiked ? '♥' : '♡'}</span>
                <span>Like</span>
              </button>

              <button
                onClick={toggleComments}
                className={`flex-1 h-9 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${showComments
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:border-gray-600'
                  }`}
              >
                <span>💬</span>
                <span>{place.comments_count || 0}</span>
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowReportForm(true);
                }}
                className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                title="Report"
              >
                ⚠️
              </button>
            </div>
          )}
        </div>

        {/* Comments Section */}
        {showComments && (
          <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
            {/* List */}
            <div className="max-h-48 overflow-y-auto px-4 py-3 space-y-3 custom-scrollbar">
              {loadingComments ? (
                <div className="text-center py-2 text-gray-400 text-xs">Loading...</div>
              ) : comments.length === 0 ? (
                <div className="text-center py-2 text-gray-400 text-xs italic">No comments yet.</div>
              ) : (
                comments.map((comment) => (
                  <div key={comment.id} className="bg-white dark:bg-gray-700 p-2.5 rounded-lg border border-gray-100 dark:border-gray-600 shadow-sm text-sm">
                    <div className="flex justify-between items-baseline mb-1">
                      <span className="font-bold text-gray-700 dark:text-gray-200 text-xs">{comment.author_name}</span>
                      <span className="text-[10px] text-gray-400">{formatTime(comment.created_at)}</span>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 leading-snug text-xs">{comment.content}</p>
                  </div>
                ))
              )}
            </div>

            {/* Input */}
            <form onSubmit={handleSubmitComment} className="p-3 border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
              <div className="relative">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  className="w-full pl-3 pr-10 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 transition-shadow dark:text-white dark:placeholder-gray-400"
                  disabled={submittingComment}
                />
                <button
                  type="submit"
                  disabled={!newComment.trim() || submittingComment}
                  className="absolute right-1 top-1 bottom-1 px-2 text-blue-500 hover:text-blue-600 disabled:text-gray-300 text-xs font-bold transition-colors"
                >
                  Post
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Footer: Tech Data */}
      <div className="bg-gray-50 dark:bg-gray-900/50 px-4 py-1.5 border-t border-gray-100 dark:border-gray-700/50 flex justify-between items-center text-[10px] text-gray-400">
        <span className="font-mono">
          {parseFloat(place.lat).toFixed(6)}, {parseFloat(place.lng).toFixed(6)}
        </span>
        <span>ID: {place.id}</span>
      </div>

      {/* Image Modal Portal */}
      {showImageModal && activeImage && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-[9999] p-4 animate-in fade-in"
          onClick={() => setShowImageModal(false)}
        >
          <div className="relative max-w-5xl max-h-[90vh] w-full flex flex-col items-center">
            <img
              src={activeImage}
              alt="Full view"
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 text-4xl leading-none"
            >
              ×
            </button>
            <p className="mt-4 text-white/80 text-sm font-light text-center max-w-2xl">
              {place.description}
            </p>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PinCard;
